import { useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { Helmet } from "react-helmet-async";

const countryDialingCodes = {
  US: { code: "+1", name: "United States" },
  CA: { code: "+1", name: "Canada" },
  GB: { code: "+44", name: "United Kingdom" },
  AU: { code: "+61", name: "Australia" },
  IN: { code: "+91", name: "India" },
  DE: { code: "+49", name: "Germany" },
  FR: { code: "+33", name: "France" },
  JP: { code: "+81", name: "Japan" },
  CN: { code: "+86", name: "China" },
  BR: { code: "+55", name: "Brazil" },
};
function ClinicForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("US");
  const [dialingCode, setDialingCode] = useState(
    countryDialingCodes["US"].code
  );
  const formRef = useRef(null); // Create a ref for the form

  const handleCountryChange = (e) => {
    const country = e.target.value;
    setSelectedCountry(country);
    setDialingCode(countryDialingCodes[country]?.code || "");
  };

  const sendEmail = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);

    // Build the JSON payload
    const payload = {
      first_name: formData.get("fname"),
      last_name: formData.get("lname"),
      clinic_name: formData.get("clinicName"),
      patients_count: formData.get("patients"),
      email: formData.get("email"),
      contact_number: Number(formData.get("contact")),
      country: selectedCountry,
      comments: formData.get("comment"),
    };
    console.log(payload);

    // Define the fetch promise
    const fetchPromise = fetch(
      `${process.env.REACT_APP_BACKEND_URL}/demo_request`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    // Use toast.promise for real-time feedback
    toast
      .promise(
        fetchPromise.then(async (res) => {
          const response = await res.json();
          console.log(response);
          if (!res.ok) {
            throw new Error(response.error || "Failed to submit the request");
          }
          return response; // Return the successful response
        }),
        {
          loading: "Submitting your request...",
          success: "Request submitted successfully!",
          error: (err) => err.message || "Failed to submit the request.",
        }
      )
      .then((data) => {
        console.log("Response:", data);
        // Handle success, such as redirecting or showing a confirmation
        if (formRef.current) {
          formRef.current.reset();
          setSelectedCountry("US"); // Reset the country to default
          setDialingCode(countryDialingCodes["US"].code); // Reset dialing code
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      })
      .finally(() => {
        setIsSubmitting(false); // Re-enable the button
      });
  };

  return (
    <>
      <Helmet>
        <title>IzzyAI For Clinics - AI-Powered Speech Therapy</title>
        <meta name="description"
          content="IzzyAI: For Clinics"
        />
      </Helmet>
      <div  className="pb-24">
        <div className="mt-10">
          <div>
            <p className="font-bold text-xl dark:text-white text-center">
              Boost Efficiency & Revenue for Your Practice With AI-Powered
              Therapists
            </p>
            <p className="dark:text-white mt-5 text-center">
              Schedule a demo to discover how IzzyAI can streamline processes and
              reduce costs.
            </p>
          </div>
          <div className="mt-10 mx-3 2xl:mx-32 xl:mx-32 lg:mx-32 md:mx-10 sm:mx-10 bg-[#CBD5E1] rounded-lg p-6 pb-10">
            <form className="space-y-6" onSubmit={sendEmail} ref={formRef}>
              <div className="md:flex md:space-x-6">
                <div className="md:w-1/2">
                  <label
                    htmlFor="fname"
                    className="block text-sm font-medium text-gray-700"
                  >
                    First Name*
                  </label>
                  <input
                    type="text"
                    name="fname"
                    id="fname"
                    autoComplete="fname"
                    className="mt-1 p-2 block w-full border-gray-300 rounded-md shadow-sm focus:outline-[#0CC8E8]"
                    required
                  />
                </div>
                <div className="md:w-1/2">
                  <label
                    htmlFor="lname"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Last Name*
                  </label>
                  <input
                    id="lname"
                    name="lname"
                    type="text"
                    autoComplete="lname"
                    className="mt-1 p-2 block w-full border-gray-300 rounded-md shadow-sm focus:outline-[#0CC8E8]"
                    required
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="clinicName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Healthcare Provider Name*
                </label>
                <input
                  id="clinicName"
                  name="clinicName"
                  type="text"
                  autoComplete="clinicName"
                  className="mt-1 p-2 block w-full border-gray-300 rounded-md shadow-sm focus:outline-[#0CC8E8]"
                  required
                />
              </div>
              <div className="md:flex md:space-x-6">
                <div className="md:w-1/2">
                  <label
                    htmlFor="patients"
                    className="block text-sm font-medium text-gray-700"
                  >
                    How Many Patients Do You Have?*
                  </label>
                  <input
                    id="patients"
                    name="patients"
                    type="number"
                    autoComplete="patients"
                    className="mt-1 p-2 block w-full border-gray-300 rounded-md shadow-sm focus:outline-[#0CC8E8]"
                    required
                  />
                </div>
                <div className="md:w-1/2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email*
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className="mt-1 p-2 block w-full border-gray-300 rounded-md shadow-sm focus:outline-[#0CC8E8]"
                    required
                  />
                </div>
              </div>
              <div className="md:flex md:space-x-6">
                <div className="md:w-1/2">
                  <label
                    htmlFor="contact"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Contact Number*
                  </label>
                  <div className="flex items-center">
                    <span className="mt-1 p-2   border-gray-300 rounded-r-md shadow-sm">
                      {dialingCode || "Code"}
                    </span>
                    <input
                      id="contact"
                      name="contact"
                      type="tel"
                      autoComplete="tel"
                      className="mt-1 p-2 block w-full border-gray-300 rounded-r-md shadow-sm focus:outline-[#0CC8E8]"
                      placeholder="Enter your contact number"
                      required
                    />
                  </div>
                </div>
                <div className="md:w-1/2">
                  <label
                    htmlFor="country"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Country
                  </label>
                  <select
                    id="country"
                    name="country"
                    className="mt-1 p-2 block w-full border-gray-300 rounded-md shadow-sm focus:outline-[#0CC8E8]"
                    value={selectedCountry}
                    onChange={handleCountryChange}
                  >
                    <option value="" disabled>
                      Select your country
                    </option>
                    {Object.entries(countryDialingCodes).map(
                      ([country, { name }]) => (
                        <option key={country} value={country}>
                          {name}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="comment"
                  className="block text-sm font-medium text-gray-700"
                >
                  Comments
                </label>
                <textarea
                  id="comment"
                  name="comment"
                  rows="4"
                  className="mt-1 p-2 block w-full border-gray-300 rounded-md shadow-sm focus:outline-[#0CC8E8]"
                ></textarea>
              </div>
              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-[#0CC8E8] to-[#2DEEAA] hover:from-teal-600 hover:to-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Send
                </button>
              </div>
            </form>
            <Toaster />
          </div>
        </div>
      </div>
    </>
  );
}

export default ClinicForm;
